import { useState, useEffect, useCallback } from "react";
import { CashAndBankRepositoryImpl } from "../../data/repositories/CashAndBankRepositoryImpl";
import { GetAllBankAccountsUseCase } from "../../domain/use-cases/GetAllBankAccountsUseCase";
import type { AllBankAccountsEntity } from "../../domain/entities/AllBankAccountsEntity";

const repository = new CashAndBankRepositoryImpl();
const getAllBankAccountsUseCase = new GetAllBankAccountsUseCase(repository);

export interface UseAllBankAccountsResult {
  /** Sum of balances of operating bank accounts (account_type bank) only */
  totalAvailableCash: number;
  /** Non–fixed_deposit accounts for bank cards */
  bankAccounts: AllBankAccountsEntity["bankAccounts"];
  /** Fixed deposit accounts for FD cards */
  fixedDeposits: AllBankAccountsEntity["fixedDeposits"];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAllBankAccounts(): UseAllBankAccountsResult {
  const [totalAvailableCash, setTotalAvailableCash] = useState(0);
  const [bankAccounts, setBankAccounts] = useState<
    AllBankAccountsEntity["bankAccounts"]
  >([]);
  const [fixedDeposits, setFixedDeposits] = useState<
    AllBankAccountsEntity["fixedDeposits"]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const entity = await getAllBankAccountsUseCase.execute();
      setTotalAvailableCash(entity.totalAvailableCash);
      setBankAccounts(entity.bankAccounts);
      setFixedDeposits(entity.fixedDeposits);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load bank accounts";
      setError(message);
      setTotalAvailableCash(0);
      setBankAccounts([]);
      setFixedDeposits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return {
    totalAvailableCash,
    bankAccounts,
    fixedDeposits,
    loading,
    error,
    refetch: fetchAccounts,
  };
}
